import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import styles from './styles.module.css';
import { ConditionData } from '../types';
import { useTranslation } from 'react-i18next';

interface ConditionOfThePropertyTableProps {
    data?: ConditionData[];
}

interface ChartDataItem {
    year: number;
    [key: string]: number;
}

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className={styles.customTooltip}>
                <p>{`Год: ${label}`}</p>
                {payload.map((entry: any) => (
                    <p key={entry.name}>{`${entry.name}: ${entry.value}`}</p>
                ))}
            </div>
        );
    }
    return null;
};


const COLOR_PALETTE = [
   
    '#4CAF50', // Зеленый
    '#8BC34A', // Светло-зеленый
    '#009688', // Бирюзовый
    '#00796B', // Темно-бирюзовый
    '#2E7D32', // Темно-зеленый
    
    
    '#2196F3', // Синий
    '#1976D2', // Темно-синий
    '#0D47A1', // Очень темно-синий
    '#03A9F4', // Голубой
    '#00BCD4', // Светло-голубой
    
   
    '#9C27B0', // Фиолетовый
    '#673AB7', // Темно-фиолетовый
    '#3F51B5', // Индиго
    '#7B1FA2', // Темно-пурпурный
    '#6A1B9A', // Очень темно-фиолетовый
    
   
    '#F44336', // Красный
    '#E91E63', // Розовый
    '#FF5722', // Оранжевый
    '#FF9800', // Светло-оранжевый
    '#795548', // Коричневый
    
   
    '#FFC107', // Желтый
    '#FDD835', // Светло-желтый
    '#FFB300', // Янтарный
    '#FB8C00', // Темно-оранжевый
    '#F57C00', // Очень темно-оранжевый
    
    
    '#9E9E9E', 
    '#607D8B', // Сине-серый
    '#455A64', // Темно-сине-серый
    '#616161', // Темно-серый
    '#424242', // Очень темно-серый
];


const PREDEFINED_COLORS: Record<string, string> = {
    'Новое': '#4CAF50',
    'Хорошее': '#8BC34A',
    'Удовлетворительное': '#FFC107',
    'Неудовлетворительное': '#FF5722',
    'Аварийное': '#F44336',
    'Непригодное к использованию': '#795548',
    'Неизвестно': '#9E9E9E'
};

const getColorForCondition = (condition: string, index: number): string => {
    
    if (PREDEFINED_COLORS[condition]) {
        return PREDEFINED_COLORS[condition];
    }
  
    return COLOR_PALETTE[index % COLOR_PALETTE.length];
};

export const ConditionOfThePropertyTable: React.FC<ConditionOfThePropertyTableProps> = ({ data }) => {
    const {t} = useTranslation()
    if (!data || data.length === 0) {
        return null;
    }

    const chartData: ChartDataItem[] = data.map(item => ({
        year: item.year,
        ...item.conditionCounts
    }));

    const conditions = Object.keys(data[0].conditionCounts);

    
    const totals = chartData.map(yearData => {
        const total = conditions.reduce((sum, condition) => sum + (yearData[condition] || 0), 0);
        return { year: yearData.year, total };
    });

    return (
        <div className={styles.container}>
            <div className={styles.title}>
                <span>{t('Condition of property')}</span>
            </div>
         
            <div className={styles.totalInfo}>
                {totals.map(({ year, total }) => (
                    <div key={year} className={styles.totalItem}>
                        <span>{t('Total number of objects in')} {year} {t('year')}: {total}</span>
                    </div>
                ))}
            </div>
            <div className={styles.chart}>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                        data={chartData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 15,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="year" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        {conditions.map((condition, index) => (
                            <Bar
                                key={condition}
                                dataKey={condition}
                                name={condition}
                                fill={getColorForCondition(condition, index)}
                                stackId="a"
                                barSize={60}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};